import { useCallback, useEffect, useRef, useState } from 'react';
import type { Item } from './navigation';

const SCROLL_STEP_RATIO = 0.8;

export function useNavigation(items: Item[]) {
  const itemsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollToPrev, setCanScrollToPrev] = useState(false);
  const [canScrollToNext, setCanScrollToNext] = useState(false);

  const updateScrollArrowsVisibility = useCallback(() => {
    const container = itemsContainerRef.current;
    if (!container) {
      return;
    }

    setCanScrollToPrev(container.scrollLeft > 0);
    setCanScrollToNext(container.scrollLeft + container.clientWidth < container.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const container = itemsContainerRef.current;
    if (!container) {
      return;
    }

    updateScrollArrowsVisibility();

    const resizeObserver = new ResizeObserver(updateScrollArrowsVisibility);
    resizeObserver.observe(container);
    container.addEventListener('scroll', updateScrollArrowsVisibility);

    return () => {
      resizeObserver.disconnect();
      container.removeEventListener('scroll', updateScrollArrowsVisibility);
    };
  }, [items, updateScrollArrowsVisibility]);

  const scrollItems = (direction: 'prev' | 'next') => {
    const container = itemsContainerRef.current;
    if (!container) {
      return;
    }

    const scrollAmount = container.clientWidth * SCROLL_STEP_RATIO;
    container.scrollBy({ left: direction === 'next' ? scrollAmount : -scrollAmount, behavior: 'smooth' });
  };

  return {
    itemsContainerRef,
    canScrollToPrev,
    canScrollToNext,
    scrollItems,
  };
}
